import http from './http'
import { getMediaOrigin } from './config'

/** 列表一次拉全量（Laravel 分页：page / per_page） */
const LIST_PAGE_PARAMS = { page: 1, per_page: 999 }
let homeProductsCache = null
let homeProductsPromise = null
let productCategoriesCache = null
let productCategoriesPromise = null
const productsByCategoryCache = new Map()
const productsByCategoryPromise = new Map()
const productDetailCache = new Map()
const productDetailPromise = new Map()

function unwrapArray(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  const d = payload.data
  if (Array.isArray(d)) return d
  if (d && typeof d === 'object' && Array.isArray(d.data)) return d.data
  return []
}

function resolveStorageUrl(path) {
  if (!path || typeof path !== 'string') return ''
  const t = path.trim()
  if (!t) return ''
  // 兼容：后端可能直接返回绝对地址（包含 /storage/uploads/...），前端期望去掉 /storage/
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:')) {
    // 只要包含 /storage/uploads 就统一替换
    if (/\/storage\/uploads/i.test(t)) return t.replace(/\/storage\/uploads/ig, '/uploads')
    return t
  }
  const base = getMediaOrigin().replace(/\/$/, '')
  // 兼容：后端有时会把上传目录写成 storage/uploads/...，前端展示期望去掉 storage/
  if (t.startsWith('/storage/uploads') || t.startsWith('storage/uploads')) {
    const rest = t.replace(/^\/?storage\//, '') // uploads/...
    return `${base}/${rest.replace(/^\//, '')}`
  }
  if (t.startsWith('/storage/') || t.startsWith('storage/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  if (t.startsWith('/uploads/') || t.startsWith('uploads/')) {
    const p = t.startsWith('/') ? t : `/${t}`
    return `${base}${p}`
  }
  return `${base}/storage/${t.replace(/^\//, '')}`
}

function resolveHtmlUrl(path) {
  if (!path || typeof path !== 'string') return path
  const t = path.trim()
  if (!t) return t
  if (/^https?:\/\//i.test(t) || t.startsWith('//') || t.startsWith('data:') || t.startsWith('#')) {
    if (/\/storage\/uploads/i.test(t)) return t.replace(/\/storage\/uploads/ig, '/uploads')
    return t
  }
  const base = getMediaOrigin().replace(/\/$/, '')
  if (t.startsWith('/storage/uploads') || t.startsWith('storage/uploads')) {
    const rest = t.replace(/^\/?storage\//, '') // uploads/...
    return `${base}/${rest.replace(/^\//, '')}`
  }
  return `${base}/${t.replace(/^\//, '')}`
}

function normalizeRichHtml(raw) {
  if (typeof raw !== 'string') return ''
  const html = raw.trim()
  if (!html) return ''
  return html.replace(/(src|href|data-href)=["']([^"']+)["']/gi, (_m, attr, url) => {
    const resolved = resolveHtmlUrl(url)
    return `${attr}="${resolved}"`
  })
}

function normalizeCategory(row) {
  if (!row || typeof row !== 'object') return null
  const id = row.id ?? row.category_id
  if (id == null) return null
  const name = row.name || row.title || row.category_name || '未命名'
  return {
    id: Number(id) || id,
    name,
    title: row.title || name,
    name_en: row.name_en || row.nameEn || row.subtitle_en || '',
    slug: row.slug || row.code || String(id),
    icon: row.icon || ''
  }
}

function normalizeProduct(row) {
  if (!row || typeof row !== 'object') return null
  const cover =
    row.cover_photo_path ||
    row.cover ||
    row.image ||
    row.pic ||
    row.image_url ||
    row.imageUrl ||
    ''
  const cat = row.product_category || row.category
  const categoryName =
    (cat && typeof cat === 'object' && (cat.name || cat.title)) ||
    (typeof row.category_name === 'string' && row.category_name) ||
    (typeof row.category === 'string' && row.category) ||
    ''
  return {
    id: row.id ?? row.product_id,
    title: row.title || row.name || row.product_name || '产品',
    subtitle: row.subtitle || row.description || row.summary || '',
    categoryName: typeof categoryName === 'string' ? categoryName : '',
    image: resolveStorageUrl(typeof cover === 'string' ? cover : ''),
    link: row.link || row.url || row.href || ''
  }
}

/** 从接口各种形态中拆出产品行数组 */
function extractProductRows(raw) {
  if (raw == null) return []
  if (Array.isArray(raw)) return raw
  if (typeof raw !== 'object') return []

  if (Array.isArray(raw.products)) return raw.products
  if (Array.isArray(raw.items)) return raw.items
  if (Array.isArray(raw.list)) return raw.list
  if (Array.isArray(raw.records)) return raw.records

  const inner = raw.data
  if (Array.isArray(inner)) return inner
  if (inner && typeof inner === 'object') {
    if (Array.isArray(inner.data)) return inner.data
    if (Array.isArray(inner.products)) return inner.products
  }

  const keys = Object.keys(raw)
  if (keys.length === 1 && Array.isArray(raw[keys[0]])) return raw[keys[0]]

  return [raw]
}

/** 详情页：长文案 / 富文本 */
export function normalizeProductDetail(row) {
  const base = normalizeProduct(row)
  if (!base) return null
  const intro =
    (typeof row.intro === 'string' && row.intro) ||
    (typeof row.summary === 'string' && row.summary) ||
    (typeof base.subtitle === 'string' ? base.subtitle : '') ||
    ''
  const raw =
    row.body ||
    row.content_html ||
    row.html_content ||
    row.content ||
    row.detail ||
    ''
  const s = typeof raw === 'string' ? raw.trim() : ''
  const isHtml = s.length > 0 && /<[a-z][\s\S]*>/i.test(s)
  return {
    ...base,
    intro,
    contentHtml: isHtml ? normalizeRichHtml(s) : '',
    contentPlain: !isHtml && s ? s : ''
  }
}

export const FALLBACK_CATEGORIES = [
  { id: 1, slug: '1', name: '物联网产品', name_en: 'AIoT', icon: '📡' },
  { id: 2, slug: '2', name: 'AI赋能硬件产品', name_en: 'AI Hardware', icon: '🖥️' },
  { id: 3, slug: '3', name: 'AI视觉及大模型产品', name_en: 'Vision & LLM', icon: '👁️' },
  { id: 4, slug: '4', name: '机器人产品', name_en: 'Robotics', icon: '🤖' }
]

export const FALLBACK_PRODUCTS = [
  {
    id: 1,
    title: '物联网产品',
    subtitle: 'RFID、门禁闸机、环境感知等设备联网与统一管理。',
    image: '',
    link: ''
  },
  {
    id: 2,
    title: 'AI 赋能硬件',
    subtitle: '借还书机、检索屏、盘点车等终端智能化升级。',
    image: '',
    link: ''
  },
  {
    id: 3,
    title: 'AI 视觉及大模型',
    subtitle: '空间感知、行为分析、智能问答与推荐能力。',
    image: '',
    link: ''
  },
  {
    id: 4,
    title: '机器人与自动化',
    subtitle: '分拣、搬运与巡检等场景自动化协同调度。',
    image: '',
    link: ''
  }
]

/**
 * 首页产品列表
 * GET /api/v1/products （Laravel: Route::get('/products', ... index)）
 */
export async function fetchProducts() {
  if (Array.isArray(homeProductsCache)) return homeProductsCache.map((p) => ({ ...p }))
  if (homeProductsPromise) return homeProductsPromise.then((list) => list.map((p) => ({ ...p })))

  homeProductsPromise = (async () => {
    try {
      const { data } = await http.get('/api/v1/products', { params: LIST_PAGE_PARAMS })
      const raw = data?.data !== undefined ? data.data : data
      let rows = []
      if (Array.isArray(raw)) {
        rows = raw
      } else if (raw && typeof raw === 'object') {
        if (Array.isArray(raw.products)) rows = raw.products
        else if (Array.isArray(raw.items)) rows = raw.items
        else if (Array.isArray(raw.list)) rows = raw.list
        else rows = [raw]
      }
      const mapped = rows.map(normalizeProduct).filter(Boolean)
      if (mapped.length) return mapped
    } catch (e) {
      console.warn('[products] 首页列表接口失败，使用本地数据', e)
    }
    return FALLBACK_PRODUCTS.map((p) => ({ ...p }))
  })()
    .then((list) => {
      homeProductsCache = list
      return list
    })
    .finally(() => {
      homeProductsPromise = null
    })

  return homeProductsPromise.then((list) => list.map((p) => ({ ...p })))
}

/**
 * 产品分类列表
 * GET /api/v1/product_categories
 */
export async function fetchProductCategories() {
  if (Array.isArray(productCategoriesCache)) return productCategoriesCache.map((c) => ({ ...c }))
  if (productCategoriesPromise) return productCategoriesPromise.then((list) => list.map((c) => ({ ...c })))

  productCategoriesPromise = (async () => {
    try {
      const { data } = await http.get('/api/v1/product_categories')
      const raw = data?.data !== undefined ? data.data : data
      const list = unwrapArray({ data: raw })
      const mapped = list.map(normalizeCategory).filter(Boolean)
      if (mapped.length) return mapped
    } catch (e) {
      console.warn('[products] 分类接口失败，使用本地分类', e)
    }
    return FALLBACK_CATEGORIES.map((c) => ({ ...c }))
  })()
    .then((list) => {
      productCategoriesCache = list
      return list
    })
    .finally(() => {
      productCategoriesPromise = null
    })

  return productCategoriesPromise.then((list) => list.map((c) => ({ ...c })))
}

function mapProductRows(rows) {
  return rows.map(normalizeProduct).filter(Boolean)
}

/**
 * 按分类 id 拉取产品列表（与后端约定：GET /api/v1/products/{分类id}）。
 * 每次切换分类只发这一条请求；首页/其它处仍用 fetchProducts() 拉全量。
 */
export async function fetchProductsByCategory(categoryId) {
  const id = categoryId
  if (id == null || id === '') return []
  const key = String(id)
  if (productsByCategoryCache.has(key)) {
    return productsByCategoryCache.get(key).map((p) => ({ ...p }))
  }
  if (productsByCategoryPromise.has(key)) {
    return productsByCategoryPromise.get(key).then((list) => list.map((p) => ({ ...p })))
  }

  const loadRows = (payload) => {
    const raw = payload?.data !== undefined ? payload.data : payload
    return extractProductRows(raw)
  }

  const req = (async () => {
    try {
      const { data } = await http.get(`/api/v1/products/${id}`, { params: LIST_PAGE_PARAMS })
      const rows = loadRows(data)
      let list = mapProductRows(rows)
      if (list.length <= 1 && rows.length === 1 && rows[0] && typeof rows[0] === 'object') {
        const nested = extractProductRows(rows[0])
        if (nested.length > 1) list = mapProductRows(nested)
      }
      return list
    } catch (e) {
      console.warn('[products] products/:id 失败', id, e)
      return []
    }
  })()
    .then((list) => {
      productsByCategoryCache.set(key, list)
      return list
    })
    .finally(() => {
      productsByCategoryPromise.delete(key)
    })

  productsByCategoryPromise.set(key, req)
  return req.then((list) => list.map((p) => ({ ...p })))
}

/**
 * 单个产品详情（后端约定：GET /api/v1/products/item/{id}）
 * 保留旧路径兜底，兼容后端未同步时的过渡期。
 */
export async function fetchProductDetail(productId) {
  const id = productId
  if (id == null || id === '') return null
  const key = String(id)
  if (productDetailCache.has(key)) {
    const cached = productDetailCache.get(key)
    return cached ? { ...cached } : null
  }
  if (productDetailPromise.has(key)) {
    return productDetailPromise.get(key).then((d) => (d ? { ...d } : null))
  }

  const tryPaths = [`/api/v1/products/item/${id}`, `/api/v1/product/${id}`, `/api/v1/products/${id}`]

  const req = (async () => {
    for (const url of tryPaths) {
      try {
        const { data } = await http.get(url)
        const raw = data?.data !== undefined ? data.data : data
        if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
          const detail = normalizeProductDetail(raw)
          if (detail) return detail
        }
      } catch (e) {
        if (url === tryPaths[tryPaths.length - 1]) {
          console.warn('[products] 产品详情失败', id, e)
        }
      }
    }
    return null
  })()
    .then((detail) => {
      productDetailCache.set(key, detail)
      return detail
    })
    .finally(() => {
      productDetailPromise.delete(key)
    })

  productDetailPromise.set(key, req)
  return req.then((d) => (d ? { ...d } : null))
}
